/**
 * Cloudflare R2 Storage Service
 * 
 * Handles uploads to Cloudflare R2 (S3-compatible) for media files.
 * Supports photos, videos, and voice messages with compression and progress tracking.
 */

import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

/**
 * The current user's access token. The r2-upload/r2-delete functions now
 * require an authenticated user, so requests must carry the user's JWT rather
 * than the anon key. Falls back to the anon key only if there is no session
 * (the function will then reject with 401, surfacing a clear auth error).
 */
async function r2AuthToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;
}

export interface R2UploadOptions {
  folder?: string;
  maxSize?: number; // in bytes
  compress?: boolean;
  generateThumbnail?: boolean;
  onProgress?: (progress: number) => void;
}

export interface R2UploadResult {
  url: string;
  path: string;
  size: number;
  type: string;
  thumbnailUrl?: string;
  duration?: number; // for video/audio
}

export class R2StorageService {
  private static instance: R2StorageService;
  private readonly R2_ACCOUNT_ID: string;
  private readonly R2_ACCESS_KEY_ID: string;
  private readonly R2_SECRET_ACCESS_KEY: string;
  private readonly R2_BUCKET_NAME: string;
  private readonly R2_PUBLIC_URL: string;
  private readonly R2_ENDPOINT: string;

  private constructor() {
    // SECURITY (2026-07-04): R2 secret credentials must NOT ship in the browser
    // bundle. Uploads/deletes go through the r2-upload / r2-delete edge
    // functions, which hold the secret server-side. The browser only needs
    // public config (account id for the endpoint, bucket, public URL).
    // See docs/reviews/2026-07-04-security-audit.md.
    this.R2_ACCOUNT_ID = import.meta.env.VITE_R2_ACCOUNT_ID || '';
    this.R2_ACCESS_KEY_ID = '';
    this.R2_SECRET_ACCESS_KEY = '';
    this.R2_BUCKET_NAME = import.meta.env.VITE_R2_BUCKET_NAME || 'strokone';
    this.R2_PUBLIC_URL = import.meta.env.VITE_R2_PUBLIC_URL || '';
    // S3-compatible endpoint (used server-side only; kept for reference)
    this.R2_ENDPOINT = this.R2_ACCOUNT_ID
      ? `https://${this.R2_ACCOUNT_ID}.r2.cloudflarestorage.com`
      : '';
  }

  static getInstance(): R2StorageService {
    if (!R2StorageService.instance) {
      R2StorageService.instance = new R2StorageService();
    }
    return R2StorageService.instance;
  }

  /**
   * Upload file to R2 via backend proxy (recommended for security)
   * The backend handles the actual R2 upload to keep credentials secure
   */
  async uploadFile(
    file: File,
    options: R2UploadOptions = {}
  ): Promise<R2UploadResult> {
    const maxSize = options.maxSize || 100 * 1024 * 1024; // 100MB default

    if (file.size > maxSize) {
      throw new Error(`File size exceeds maximum allowed size of ${maxSize / (1024 * 1024)}MB`);
    }

    // Process file (compress if needed)
    let processedFile = file;
    if (options.compress && this.isImage(file)) {
      processedFile = await this.compressImage(file);
    }

    // Generate file path
    const filePath = this.generateFilePath(processedFile, options.folder);

    // Create form data
    const formData = new FormData();
    formData.append('file', processedFile);
    formData.append('path', filePath);
    formData.append('folder', options.folder || 'media');

    try {
      // Get Supabase URL and use Edge Function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('VITE_SUPABASE_URL not configured');
      }
      
      // Upload via Supabase Edge Function
      const response = await fetch(`${supabaseUrl}/functions/v1/r2-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await r2AuthToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(error.error || `Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.url) {
        throw new Error('Upload succeeded but no URL returned');
      }

      // Generate thumbnail if requested
      let thumbnailUrl: string | undefined;
      if (options.generateThumbnail && this.isImage(processedFile)) {
        try {
          const thumbnail = await this.generateThumbnail(processedFile);
          const thumbnailPath = this.generateThumbnailPath(filePath);
          
          const thumbFormData = new FormData();
          thumbFormData.append('file', thumbnail);
          thumbFormData.append('path', thumbnailPath);
          thumbFormData.append('folder', options.folder || 'media');

          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const thumbResponse = await fetch(`${supabaseUrl}/functions/v1/r2-upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${await r2AuthToken()}`,
            },
            body: thumbFormData,
          });

          if (thumbResponse.ok) {
            const thumbResult = await thumbResponse.json();
            thumbnailUrl = thumbResult.url;
          }
        } catch (thumbError) {
          console.warn('Failed to generate thumbnail:', thumbError);
        }
      }

      return {
        url: result.url,
        path: filePath,
        size: processedFile.size,
        type: processedFile.type,
        thumbnailUrl,
      };
    } catch (error) {
      console.error('R2 upload failed:', error);
      throw error;
    }
  }

  /**
   * Upload file directly to R2 (client-side, requires public R2 bucket)
   * Use this only if you have a public bucket with CORS configured
   */
  async uploadFileDirect(
    file: File,
    options: R2UploadOptions = {}
  ): Promise<R2UploadResult> {
    // This would require AWS SDK or direct S3 API calls
    // For security, we recommend using the backend proxy instead
    throw new Error('Direct upload not implemented. Use uploadFile() with backend proxy.');
  }

  /**
   * Delete file from R2
   */
  async deleteFile(path: string): Promise<void> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('VITE_SUPABASE_URL not configured');
      }
      
      const response = await fetch(`${supabaseUrl}/functions/v1/r2-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await r2AuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }
    } catch (error) {
      console.error('R2 delete failed:', error);
      throw error;
    }
  }

  /**
   * Get public URL for a file
   */
  getPublicUrl(path: string): string {
    if (this.R2_PUBLIC_URL) {
      return `${this.R2_PUBLIC_URL}/${path}`;
    }
    // Fallback: construct URL from endpoint and bucket name
    return `${this.R2_ENDPOINT}/${this.R2_BUCKET_NAME}/${path}`;
  }

  // Helper methods
  private generateFilePath(file: File, folder?: string): string {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2);
    const extension = file.name.split('.').pop();
    const fileName = `${timestamp}-${randomId}.${extension}`;

    return folder ? `${folder}/${fileName}` : fileName;
  }

  private generateThumbnailPath(originalPath: string): string {
    const parts = originalPath.split('.');
    const extension = parts.pop();
    const basePath = parts.join('.');
    return `${basePath}_thumb.${extension}`;
  }

  private isImage(file: File): boolean {
    return file.type.startsWith('image/');
  }

  private async compressImage(file: File, quality = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const maxWidth = 1920;
        const maxHeight = 1080;
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Image compression failed'));
            }
          },
          file.type,
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image for compression'));
      img.src = URL.createObjectURL(file);
    });
  }

  private async generateThumbnail(file: File, size = 150): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = size;
        canvas.height = size;

        const minDimension = Math.min(img.width, img.height);
        const x = (img.width - minDimension) / 2;
        const y = (img.height - minDimension) / 2;

        ctx?.drawImage(
          img,
          x, y, minDimension, minDimension,
          0, 0, size, size
        );

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const thumbnailFile = new File([blob], `thumb_${file.name}`, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(thumbnailFile);
            } else {
              reject(new Error('Thumbnail generation failed'));
            }
          },
          file.type,
          0.7
        );
      };

      img.onerror = () => reject(new Error('Failed to load image for thumbnail'));
      img.src = URL.createObjectURL(file);
    });
  }
}
